/**
 * "Fundir" (the owner, 29/10/2026; docs/refactor/62): in the desktop app the IDE shrinks to the prompt and its
 * answer, in a small window always in front of Windows, so the person sends a command that opens programs or writes
 * documents and watches it happen behind. "Restaurar" brings the whole IDE back. The window itself is changed by the
 * app (fuse.rs); this keeps the page's side — the layout — in step with it. Not kept across restarts: the app always
 * opens whole.
 */

import { useSyncExternalStore } from 'react'
import { isDesktop } from './desktop'

export const FUSE_EVENT = 'devpulse:fused'

let fused = false
let pinned = true

async function invoke(command: string, args: Record<string, unknown>): Promise<void> {
  const { invoke: call } = await import('@tauri-apps/api/core')
  await call(command, args)
}

function announce() {
  document.documentElement.toggleAttribute('data-fused', fused)
  window.dispatchEvent(new Event(FUSE_EVENT))
}

export function isFused(): boolean {
  return fused
}

export function isPinned(): boolean {
  return pinned
}

/** Fuses (true) or restores (false) this window. Desktop only: a browser tab cannot float over the system. */
export async function setFused(on: boolean): Promise<void> {
  if (!isDesktop() || on === fused) return
  await invoke('window_fuse', { fused: on })
  fused = on
  pinned = true
  announce()
}

export function toggleFused(): Promise<void> {
  return setFused(!fused)
}

/** "Sempre à frente", while fused: off lets other windows cover it. */
export async function setPinned(on: boolean): Promise<void> {
  if (!isDesktop() || !fused) return
  await invoke('window_pin', { on })
  pinned = on
  announce()
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(FUSE_EVENT, onChange)
  return () => window.removeEventListener(FUSE_EVENT, onChange)
}

export function useFused(): boolean {
  return useSyncExternalStore(subscribe, isFused, () => false)
}

export function usePinned(): boolean {
  return useSyncExternalStore(subscribe, isPinned, () => true)
}
